import type { TaskState } from "@fistos/shared/api";
import { Battery, BatteryCharging, BatteryLow } from "lucide-solid";
import { Show, type JSX } from "solid-js";
import { Badge } from "@/components/ui/badge";
import { cx } from "@/lib/cva";

type Tone = "neutral" | "good" | "warn" | "bad" | "busy";

const TONES: Record<Tone, string> = {
  neutral: "bg-secondary text-secondary-foreground border-transparent",
  good: "bg-success/12 text-success border-success/25",
  warn: "bg-warning/15 text-warning border-warning/30",
  bad: "bg-destructive/10 text-destructive border-destructive/25",
  busy: "bg-primary/8 text-foreground border-primary/15",
};

export function ToneBadge(props: { tone: Tone; children: JSX.Element; class?: string }) {
  return (
    <Badge variant="outline" class={cx(TONES[props.tone], props.class)}>
      {props.children}
    </Badge>
  );
}

export function OnlineBadge(props: { online: boolean }) {
  return (
    <ToneBadge tone={props.online ? "good" : "neutral"}>
      <span class={cx("size-1.5 rounded-full", props.online ? "bg-success" : "bg-muted-foreground/50")} />
      {props.online ? "Online" : "Offline"}
    </ToneBadge>
  );
}

const TASK_TONE: Record<TaskState, Tone> = {
  pending: "busy",
  running: "busy",
  succeeded: "good",
  failed: "bad",
  cancelled: "neutral",
  timed_out: "warn",
  step_limit: "warn",
  rejected: "bad",
};

const TASK_LABEL: Record<TaskState, string> = {
  pending: "Starting",
  running: "Running",
  succeeded: "Done",
  failed: "Failed",
  cancelled: "Cancelled",
  timed_out: "Timed out",
  step_limit: "Step limit",
  rejected: "Rejected",
};

export function TaskBadge(props: { state: TaskState }) {
  return <ToneBadge tone={TASK_TONE[props.state]}>{TASK_LABEL[props.state]}</ToneBadge>;
}

/** ActionStatus name (OK, FAILED, DENIED, REJECTED, …). */
export function StatusBadge(props: { status: string }) {
  const tone = (): Tone =>
    props.status === "OK"
      ? "good"
      : props.status === "UNSPECIFIED"
        ? "neutral"
        : props.status === "TIMEOUT"
          ? "warn"
          : "bad";
  return <ToneBadge tone={tone()}>{props.status === "OK" ? "OK" : props.status.toLowerCase()}</ToneBadge>;
}

/** ApprovalClass name (AUTO, CONFIRM, DENY). */
export function ApprovalClassBadge(props: { value: string }) {
  return (
    <Show when={props.value !== "UNSPECIFIED"}>
      <ToneBadge tone={props.value === "AUTO" ? "neutral" : props.value === "CONFIRM" ? "warn" : "bad"}>
        {props.value.toLowerCase()}
      </ToneBadge>
    </Show>
  );
}

export function BatteryBadge(props: { percent: number; charging: boolean }) {
  const Icon = () => (props.charging ? <BatteryCharging /> : props.percent <= 15 ? <BatteryLow /> : <Battery />);
  return (
    <ToneBadge tone={props.percent <= 15 && !props.charging ? "warn" : "neutral"}>
      <Icon />
      {props.percent}%
    </ToneBadge>
  );
}
